/**
 * Operational UI Utils - Frontend
 * @description Utility functions for operational dashboard UI components
 * These utilities provide consistent formatting, styling, and presentation logic
 */

import {
  UIOperationPerformance,
  UILectureEvent,
  UIOperationalDevice,
  UIOperation,
  UILecture,
  OperationPerformance,
  LectureEventType,
} from '@/types/operational-ui.types';
import {
  CheckCircle,
  Circle,
  AlertCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  TrendingDown,
  // Square,
  Ruler,
  // Plug,
  Check,
  X,
  HelpCircle,
  Pause,
  // Hand,
  TriangleAlert,
  CloudAlert,
  CalendarSync,
  CircleUserRound,
  DraftingCompass,
  Hourglass,
  icons,
  LucideIcon,
} from 'lucide-react';
import { EventTypeResponse } from '@/types';

// ============================================================================
// COLOR UTILITIES
// ============================================================================

/**
 * Get Tailwind CSS classes for performance indicators
 * Supports both backend types and UI types
 */
export const getPerformanceStyles = (
  performance: OperationPerformance | UIOperationPerformance | string
) => {
  const styles = {
    Perfect: {
      // Backend: 'Perfect'
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      ring: 'ring-green-500/20',
      icon: CheckCircle,
      color: '#10b981',
    },
    Good: {
      // Backend: 'Good'
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      ring: 'ring-blue-500/20',
      icon: Circle,
      color: '#3b82f6',
    },
    Regular: {
      // Backend: 'Regular'
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      ring: 'ring-yellow-500/20',
      icon: AlertCircle,
      color: '#f59e0b',
    },
    Concerning: {
      // Backend: 'Concerning'
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-200',
      ring: 'ring-orange-500/20',
      icon: AlertTriangle,
      color: '#f97316',
    },
    Danger: {
      // Backend: 'Danger'
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      ring: 'ring-red-500/20',
      icon: XCircle,
      color: '#ef4444',
    },
    // Legacy POC values for backward compatibility
    Excellent: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      ring: 'ring-green-500/20',
      icon: CheckCircle,
      color: '#10b981',
    },
    Bad: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-200',
      ring: 'ring-orange-500/20',
      icon: AlertTriangle,
      color: '#f97316',
    },
    Dangerous: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      ring: 'ring-red-500/20',
      icon: XCircle,
      color: '#ef4444',
    },
  };

  return styles[performance as keyof typeof styles] || styles['Regular'];
};

/**
 * Get Tailwind CSS classes for event types
 * Supports both backend types and UI types
 */
export const getEventStyles = (event: LectureEventType | UILectureEvent | string) => {
  const dynamicType = dynamicEventTypeMap.get(String(event));
  if (dynamicType) {
    const icon = (icons[dynamicType.icon as keyof typeof icons] as LucideIcon) || CircleUserRound;
    return {
      bg: dynamicType.active ? 'bg-emerald-100' : 'bg-gray-200',
      text: dynamicType.active ? 'text-emerald-900' : 'text-gray-700',
      border: dynamicType.active ? 'border-emerald-200' : 'border-gray-300',
      icon,
      color: dynamicType.iconColor || dynamicType.textColor || '#374151',
      bgHex: dynamicType.bgColor || '#e5e7eb',
      textHex: dynamicType.textColor || '#374151',
      iconHex: dynamicType.iconColor || dynamicType.textColor || '#374151',
      iconName: dynamicType.icon,
      labelEs: dynamicType.labelEs,
      labelEn: dynamicType.labelEn,
      active: dynamicType.active,
    };
  }

  const styles = {
    // Backend types (official)
    SUB: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      icon: TrendingUp,
      color: '#10b981',
    },
    BAJ: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: TrendingDown,
      color: '#ef4444',
    },
    PAR: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      icon: Pause,
      color: '#6b7280',
    },
    CAL: {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      icon: Ruler,
      color: '#8b5cf6',
    },
    MAN: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      icon: CircleUserRound,
      color: '#f59e0b',
    },
    INC: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: TriangleAlert,
      color: '#ff0000',
    },
    DES: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      icon: CloudAlert,
      color: '#f59e0b',
    },
    CFE: {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      icon: CalendarSync,
      color: '#8b5cf6',
    },
    CHO: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      icon: Hourglass,
      color: '#f59e0b',
    },
    MMA: {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      icon: CircleUserRound,
      color: '#8b5cf6',
    },
    CAN: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      icon: DraftingCompass,
      color: '#f59e0b',
    },
  };

  return styles[event as keyof typeof styles] || styles['MAN']; // Default to MAN (manual/disconnected)
};

const dynamicEventTypeMap = new Map<string, EventTypeResponse>();

export const setDynamicEventTypes = (types: EventTypeResponse[]): void => {
  dynamicEventTypeMap.clear();
  for (const type of types) {
    dynamicEventTypeMap.set(type.code, type);
    dynamicEventTypeMap.set(type.code.toUpperCase(), type);
  }
};

/**
 * Get status styles for devices
 */
export const getDeviceStatusStyles = (status: 'Active' | 'Inactive') => {
  const styles = {
    Active: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      icon: Check,
    },
    Inactive: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      icon: X,
    },
  };

  return styles[status];
};

/**
 * Get connection status styles
 */
export const getConnectionStatusStyles = (status: 'Connected' | 'Disconnected' | 'Unknown') => {
  const styles = {
    Connected: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      icon: CheckCircle,
      pulse: 'animate-pulse',
    },
    Disconnected: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: XCircle,
      pulse: '',
    },
    Unknown: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      icon: HelpCircle,
      pulse: '',
    },
  };

  return styles[status];
};

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format timestamp to relative time
 */
export const formatRelativeTime = (timestamp: string): string => {
  // const now = new Date();
  const past = new Date(timestamp);
  /*
  const diffMs = now.getTime() - past.getTime();

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  */
  return past.toLocaleDateString();
};

/**
 * Format duration in minutes to human readable
 */
export const formatDuration = (minutes: number | undefined): string => {
  if (!minutes || minutes === 0) return 'N/A';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format coordinates to display string
 */
export const formatCoordinates = (lat: number, lng: number, precision: number = 6): string => {
  return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
};

/**
 * Format effectiveness percentage
 */
export const formatEffectiveness = (effectiveness: number | undefined): string => {
  if (effectiveness === undefined || effectiveness === null) return 'N/A';
  return `${(effectiveness * 100).toFixed(1)}%`;
};

/**
 * Format operation count
 */
export const formatOperationCount = (count: number | undefined): string => {
  if (!count) return '0';
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
};

/**
 * Format angle values
 */
export const formatAngle = (angle: number, unit: string = '°'): string => {
  return `${angle.toFixed(1)}${unit}`;
};

/**
 * Format angle pair (lateral, longitudinal)
 */
export const formatAnglePair = (lateral: number, longitudinal: number): string => {
  return `L: ${formatAngle(lateral)} | Long: ${formatAngle(longitudinal)}`;
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Check if coordinates are valid
 */
export const isValidCoordinates = (lat: number, lng: number): boolean => {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/**
 * Check if operation is currently active
 */
export const isOperationActive = (operation: UIOperation): boolean => {
  return operation.isActive && !operation.endDate;
};

/**
 * Check if device has recent activity
 */
export const hasRecentActivity = (
  device: UIOperationalDevice,
  hoursThreshold: number = 24
): boolean => {
  if (!device.lastConnection) return false;

  const now = new Date();
  const lastActivity = new Date(device.lastConnection);
  const hoursDiff = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);

  return hoursDiff <= hoursThreshold;
};

// ============================================================================
// SORTING UTILITIES
// ============================================================================

/**
 * Compare performance levels for sorting
 */
export const comparePerformance = (
  a: UIOperationPerformance,
  b: UIOperationPerformance
): number => {
  const order = ['Dangerous', 'Bad', 'Regular', 'Good', 'Excellent'];
  return order.indexOf(a) - order.indexOf(b);
};

/**
 * Sort operations by priority (performance + recency)
 */
export const sortOperationsByPriority = (operations: UIOperation[]): UIOperation[] => {
  return [...operations].sort((a, b) => {
    // First by performance (worst first for attention)
    const perfCompare = comparePerformance(b.performance, a.performance);
    if (perfCompare !== 0) return perfCompare;

    // Then by recency (most recent first)
    const aTime = new Date(a.timestamp).getTime();
    const bTime = new Date(b.timestamp).getTime();
    return bTime - aTime;
  });
};

// ============================================================================
// FILTERING UTILITIES
// ============================================================================

/**
 * Filter operations by date range
 */
export const filterOperationsByDateRange = (
  operations: UIOperation[],
  startDate: string,
  endDate: string
): UIOperation[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return operations.filter((op) => {
    const opDate = new Date(op.startDate);
    return opDate >= start && opDate <= end;
  });
};

/**
 * Filter lectures by event types
 */
export const filterLecturesByEvents = (
  lectures: UILecture[],
  events: UILectureEvent[]
): UILecture[] => {
  if (events.length === 0) return lectures;
  return lectures.filter((lecture) => events.includes(lecture.eventType as UILectureEvent));
};

// ============================================================================
// SEARCH UTILITIES
// ============================================================================

/**
 * Search operations by multiple fields
 */
export const searchOperations = (operations: UIOperation[], query: string): UIOperation[] => {
  if (!query.trim()) return operations;

  const searchTerm = query.toLowerCase();

  return operations.filter(
    (op) =>
      op.vehicle.toLowerCase().includes(searchTerm) ||
      op.operationIdentifier?.toLowerCase().includes(searchTerm) ||
      op.performance.toLowerCase().includes(searchTerm) ||
      op.deviceId.toLowerCase().includes(searchTerm)
  );
};

/**
 * Search lectures by multiple fields
 */
export const searchLectures = <T extends UILecture>(lectures: T[], query: string): T[] => {
  if (!query.trim()) return lectures;

  const searchTerm = query.toLowerCase();

  return lectures.filter(
    (lecture) =>
      lecture.eventType.toLowerCase().includes(searchTerm) ||
      lecture.operationId?.toLowerCase().includes(searchTerm) ||
      lecture.coordinates.latitude.toFixed(6).includes(searchTerm) ||
      lecture.coordinates.longitude.toFixed(6).includes(searchTerm)
  );
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

/**
 * Prepare data for CSV export
 */
export const prepareForCSVExport = (data: any[], fields: string[]): any[] => {
  return data.map((item) => {
    const row: any = {};
    fields.forEach((field) => {
      row[field] = item[field] || '';
    });
    return row;
  });
};

/**
 * Generate filename for exports
 */
export const generateExportFilename = (prefix: string, format: string): string => {
  const timestamp = new Date().toISOString().split('T')[0];
  return `${prefix}_${timestamp}.${format}`;
};

/**
 * Calculate map bounds for a set of operations
 */
export const calculateMapBounds = (
  operations: Array<{ coordinates: { latitude: number; longitude: number } }>
): { north: number; south: number; east: number; west: number } | null => {
  if (!operations.length) return null;

  let north = -90,
    south = 90,
    east = -180,
    west = 180;

  operations.forEach((op) => {
    const { latitude, longitude } = op.coordinates;
    if (latitude > north) north = latitude;
    if (latitude < south) south = latitude;
    if (longitude > east) east = longitude;
    if (longitude < west) west = longitude;
  });

  // Add padding
  const padding = 0.01;
  return {
    north: north + padding,
    south: south - padding,
    east: east + padding,
    west: west - padding,
  };
};
